/* The Atelier: two small custom elements for the bag. Loaded (defer) by snippets/cart-drawer.liquid and
   sections/main-cart-items.liquid; both guards below make a second copy harmless. No timers, no observers.

   <at-ship-bar> (snippets/at-ship-bar.liquid): free-shipping bar. Liquid renders it fully when the cart is in the store
   currency; for another currency it converts the store-currency threshold with window.Shopify.currency.rate (as Rebuy's
   bar and Shopify's checkout do) and fills in the message. Either way the fill slides from the instance's previous
   value on re-render, unless the shopper prefers reduced motion.

   <at-suggest> (snippets/at-cart-upsell.liquid): the bag's suggestions row. When the drawer opens (cart-drawer.js calls
   refresh()) or re-renders while open, it asks Rebuy's data source for its picks with the cart as context. Same picks
   as the server-rendered row: keep the rows, mark them as Rebuy's. Different picks: fetch the missing rows from
   Shopify (sections/at-cart-suggestion.liquid) and swap the list once. Rebuy's picks get the Rebuy line-item
   properties (rendered disabled in snippets/at-upsell-row.liquid, enabled here). Any failure keeps the server list. */

if (!customElements.get('at-ship-bar')) {
  class AtShipBar extends HTMLElement {
    static last = {};

    connectedCallback() {
      const d = this.dataset;
      let pct = Number(d.pct);
      if (this.hasAttribute('data-convert')) {
        pct = this.convert();
        if (pct == null) return;
      }
      const fill = this.querySelector('.at-ship-fill');
      const before = AtShipBar.last[d.key];
      AtShipBar.last[d.key] = pct;
      if (!fill || before == null || before === pct) return;
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
      // Slide from the old value: paint the old width first, then transition to the new one.
      fill.style.transition = 'none';
      fill.style.transform = `scaleX(${before / 100})`;
      fill.getBoundingClientRect();
      fill.style.transition = '';
      fill.style.transform = `scaleX(${pct / 100})`;
    }

    // Returns the percentage, or null (bar removed) when there is no usable rate for the cart's currency.
    convert() {
      const d = this.dataset;
      const cur = window.Shopify && window.Shopify.currency;
      const rate = cur && cur.active === d.currency ? parseFloat(cur.rate) : NaN;
      if (!(rate > 0)) {
        this.remove();
        return null;
      }
      const threshold = Math.round(Number(d.threshold) * rate);
      const total = Number(d.total);
      const left = threshold - total;
      const pct = Math.min(100, Math.floor((total * 100) / threshold));
      const template = left > 0 ? d.remaining : d.reached;
      const msg = this.querySelector('.at-ship-msg');
      if (msg) msg.innerHTML = (template || '').split('[amount]').join(AtShipBar.money(Math.max(left, 0), d.money));
      this.classList.toggle('is-done', left <= 0);
      const track = this.querySelector('[role="progressbar"]');
      if (track) track.setAttribute('aria-valuenow', pct);
      const fill = this.querySelector('.at-ship-fill');
      if (fill) fill.style.transform = `scaleX(${pct / 100})`;
      this.setAttribute('data-ready', '');
      return pct;
    }

    // Formats cents like Shopify's money filter did for the sample 1234567.89 rendered in the cart's currency, e.g.
    // "$1,234,567.89", "1.234.567,89 €", "¥1,234,568".
    static money(cents, sample) {
      const m = /\d[\d.,'\s  ]*\d/.exec(sample || '');
      const amount = cents / 100;
      if (!m) return amount.toFixed(2);
      const digits = m[0];
      const decSep = /\d{2}$/.test(digits) && /[^\d]\d{2}$/.test(digits) ? digits.charAt(digits.length - 3) : '';
      const thouSep = /^\d[^\d]/.test(digits) && digits.charAt(1) !== decSep ? digits.charAt(1) : '';
      const fixed = decSep ? amount.toFixed(2) : String(Math.round(amount));
      const [whole, frac] = fixed.split('.');
      const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, thouSep);
      return sample.slice(0, m.index) + grouped + (decSep ? decSep + frac : '') + sample.slice(m.index + digits.length);
    }
  }
  customElements.define('at-ship-bar', AtShipBar);
}

if (!customElements.get('at-suggest')) {
  class AtSuggest extends HTMLElement {
    static picks = new Map(); // Rebuy request URL -> promise of product list
    static rows = new Map(); // product handle -> promise of row HTML ('' when Shopify renders nothing)

    constructor() {
      super();
      this.warmImages = this.warmImages.bind(this);
      // A size select's change must not reach <cart-drawer-items>: Dawn's CartItems reads every change inside it as a
      // quantity edit and posts /cart/change for a line that does not exist.
      this.addEventListener('change', (event) => event.stopPropagation());
    }

    connectedCallback() {
      const drawer = this.closest('cart-drawer');
      if (drawer && drawer.classList.contains('active')) this.refresh();
      if (window.atWarmup?.idle) this.warmImages();
      else document.addEventListener('at:warmup-done', this.warmImages, { once: true });
    }

    disconnectedCallback() {
      document.removeEventListener('at:warmup-done', this.warmImages);
    }

    // The optional photos wait for the page's warm-up, or bag intent, instead of starting behind an opening drawer.
    warmImages() {
      const connection = navigator.connection;
      if (connection && (connection.saveData || /(^|-)2g$/.test(connection.effectiveType || ''))) return;
      this.querySelectorAll('.at-upsell-pic img').forEach((img) => {
        img.fetchPriority = 'low';
        img.loading = 'eager';
      });
    }

    refresh() {
      if (this.started) return;
      const source = this.querySelector('script[data-rebuy-request]');
      if (!source) return;
      this.started = true;
      let req;
      try {
        req = JSON.parse(source.textContent);
      } catch (e) {
        return;
      }
      const url = AtSuggest.requestUrl(req);
      if (!AtSuggest.picks.has(url)) {
        AtSuggest.picks.set(
          url,
          fetch(url, { credentials: 'omit' })
            .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`Rebuy ${r.status}`))))
            .then((json) => (Array.isArray(json && json.data) ? json.data : []))
        );
      }
      AtSuggest.picks
        .get(url)
        .then((products) => this.apply(products, req))
        .catch(() => AtSuggest.picks.delete(url)); // silent: the server-rendered row stays
    }

    // The query Rebuy's own cart widget sends (rebuy.js getWidgetProducts / formatCartConfig), without the cart token.
    static requestUrl(req) {
      const q = new URLSearchParams();
      const items = req.items || [];
      const rate = parseFloat(window.Shopify && window.Shopify.currency && window.Shopify.currency.rate) || 1;
      const subtotal = req.currency === req.shopCurrency ? req.subtotal : Math.round(req.subtotal / rate);
      q.set('key', req.key);
      q.set('limit', req.limit);
      q.set('shopify_product_ids', items.map((i) => i.product_id).join(','));
      q.set('shopify_variant_ids', items.map((i) => i.variant_id).join(','));
      q.set('cart_subtotal', subtotal);
      q.set('cart_count', items.length);
      q.set('cart_line_count', items.length);
      q.set('cart_item_count', req.itemCount);
      q.set('cart[subtotal]', subtotal);
      q.set('cart[line_count]', items.length);
      q.set('cart[item_count]', req.itemCount);
      items.forEach((item, i) => {
        q.set(`cart[items][${i}][product_id]`, item.product_id);
        q.set(`cart[items][${i}][variant_id]`, item.variant_id);
        q.set(`cart[items][${i}][quantity]`, item.quantity);
      });
      if (req.country) q.set('country_code', req.country);
      return `https://rebuyengine.com/api/v1/custom/id/${encodeURIComponent(req.list)}?${q}`;
    }

    static available(variant) {
      return (
        !variant.inventory_management ||
        variant.inventory_policy === 'continue' ||
        Number(variant.inventory_quantity) > 0
      );
    }

    async apply(products, req) {
      if (!this.isConnected) return;
      const inBag = new Set((req.items || []).map((i) => String(i.product_id)));
      const picks = products
        .filter((p) => p && p.id && p.handle && !inBag.has(String(p.id)))
        .filter((p) => (p.variants || []).some(AtSuggest.available))
        .slice(0, req.limit);
      if (!picks.length) return;

      const list = this.querySelector('.at-upsell-list');
      if (!list) return;
      const current = new Map([...list.children].map((li) => [li.dataset.productId, li]));
      const want = picks.map((p) => String(p.id));
      AtSuggest.lastPicks = want; // read by the verification scripts

      if (want.length === current.size && want.every((id, i) => [...current.keys()][i] === id)) {
        this.credit(list);
        return;
      }

      const rows = await Promise.all(picks.map((p) => current.get(String(p.id)) || this.fetchRow(p.handle)));
      const good = rows.filter(Boolean);
      if (!good.length || !this.isConnected) return;
      list.replaceChildren(...good);
      this.hidden = false;
      this.credit(list);
    }

    // Row HTML for a product the server list does not hold, rendered by Shopify in the shopper's currency.
    fetchRow(handle) {
      if (!AtSuggest.rows.has(handle)) {
        const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
        AtSuggest.rows.set(
          handle,
          fetch(`${root}products/${encodeURIComponent(handle)}?section_id=at-cart-suggestion`)
            .then((r) => (r.ok ? r.text() : ''))
            .catch(() => '')
        );
      }
      return AtSuggest.rows.get(handle).then((html) => {
        if (!html) {
          AtSuggest.rows.delete(handle); // retry next time (the proxy drops some section renders)
          return null;
        }
        const tpl = document.createElement('template');
        tpl.innerHTML = html;
        const li = tpl.content.querySelector('li.at-upsell-row');
        return li ? document.importNode(li, true) : null;
      });
    }

    credit(list) {
      list.querySelectorAll('input[data-rebuy-prop]').forEach((input) => {
        input.disabled = false;
      });
      this.dataset.source = 'rebuy';
    }
  }
  customElements.define('at-suggest', AtSuggest);
}
